import { useEffect, useRef } from 'react';
import type { ChatTurn, ProductCard as ProductCardData, PublicStoreConfig } from '../types';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import WelcomeScreen from './WelcomeScreen';

interface Props {
  turns: ChatTurn[];
  awaitingReply: boolean;
  slug: string;
  storeInitial: string;
  /** Assistant chat avatar (persona emoji, falls back to store initial). */
  personaAvatar: string;
  storeName: string;
  config: PublicStoreConfig | null;
  onAddToCart: (productId: string) => void;
  onViewMore: (product: ProductCardData) => void;
  /** Send a suggested prompt / category into the conversation. */
  onPick: (message: string) => void;
  /** Centering wrapper so the thread aligns with header/input on desktop. */
  contentClass?: string;
}

export function MessageList({
  turns,
  awaitingReply,
  slug,
  storeInitial,
  personaAvatar,
  storeName,
  config,
  onAddToCart,
  onViewMore,
  onPick,
  contentClass = 'w-full',
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, awaitingReply]);

  return (
    <div
      className="scroll-smooth-y flex-1 overflow-y-auto bg-surface px-4 py-4"
      aria-live="polite"
      aria-busy={awaitingReply}
    >
      <div className={contentClass}>
        {turns.length === 0 && !awaitingReply && (
          <WelcomeScreen
            slug={slug}
            config={config}
            storeName={storeName}
            storeInitial={storeInitial}
            onPick={onPick}
          />
        )}

        <div className="flex flex-col gap-3">
          {turns.map((turn) => (
            <MessageBubble
              key={turn.id}
              turn={turn}
              storeInitial={personaAvatar}
              onAddToCart={onAddToCart}
              onViewMore={onViewMore}
              onPick={onPick}
            />
          ))}
          {awaitingReply && <TypingIndicator initial={personaAvatar} />}
        </div>

        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default MessageList;
